import {call, put, select, takeLatest} from 'redux-saga/effects'
import get from 'lodash/get'

import * as api from '../api/api'

import {
  EDIT_LOOK_NAME,
  DELETE_LOOK_ITEM,
  GET_PARTICIPANT_LOOK,
  GET_PARTICIPANT_LOOK_FAILURE,
  UPDATE_USER_EVENT_LOOK_ITEM,
  editLookNameFailure,
  editLookNamePending,
  editLookNameSuccess,
  deleteLookItemPending,
  deleteLookItemSuccess,
  deleteLookItemFailure,
  getParticipantLookFailure,
  getParticipantLookPending,
  getParticipantLookSuccess,
  updateUserEventLookItemSuccess,
} from '../actions/looks'

import {
  GET_EVENT_LOOKS,
  getEventLooksFailure,
  getEventLooksPending,
  getEventLooksSuccess,
} from '../actions/api'

import {openErrorModal} from '../actions/userInteractions'
import {isGoodResponse} from '../api/statusCodes'

import * as events from '../store/events'
import * as user from '../store/user'

// Get Looks
// ----------------------------------------------------------------------------

export function* getEventLooksWorker({payload: {eventId}}) {
  const state = yield select()

  try {
    yield put(getEventLooksPending())

    const response = yield call(api.getEventLooks, eventId, user.id(state))

    if (isGoodResponse(response.status)) {
      const responseData = get(response, 'json', {})
      yield put(getEventLooksSuccess(responseData))
    } else {
      throw new Error(`Could not get event and order details: ${response.status}`)
    }
  } catch (e) {
    console.error('Could not get event and order details.', e.message)
    yield put(getEventLooksFailure())
  }
}

export function* getEventLooksSaga() {
  yield takeLatest(GET_EVENT_LOOKS, getEventLooksWorker)
}

// Delete Look Item
// ----------------------------------------------------------------------------

export function* deleteLookItemWorker({payload: {lookItemId, lookId}}) {
  try {
    yield put(deleteLookItemPending())

    yield api.deleteLookItem(lookItemId).then(
      api.onStatus({
        OK: ({json}) => json,
        DEFAULT: ({statusText}) => {
          throw new Error(`Not able to delete look item: ${statusText}`)
        },
      }),
    )

    yield put(deleteLookItemSuccess(lookItemId, lookId))
  } catch (e) {
    yield put(deleteLookItemFailure(e))
  }
}

export function* deleteLookItemSaga() {
  yield takeLatest(DELETE_LOOK_ITEM, deleteLookItemWorker)
}

// Get Participant Look
// ----------------------------------------------------------------------------

function* getParticipantLookFailureWorker() {
  yield put(openErrorModal())
}

export function* getParticipantLookFailureSaga() {
  yield takeLatest(GET_PARTICIPANT_LOOK_FAILURE, getParticipantLookFailureWorker)
}

export function* getParticipantLookWorker() {
  const state = yield select()
  const eventId = yield events.currentId(state)

  yield put(getParticipantLookPending())
  try {
    const response = yield call(api.getParticipantLook, eventId)
    if (isGoodResponse(response.status)) {
      const responseData = get(response, 'json', {})
      yield put(getParticipantLookSuccess(responseData))
    } else {
      throw new Error(`Could not get participant look: ${response.status}`)
    }
  } catch (e) {
    console.error('Could not get participant look.', e.message)
    yield put(getParticipantLookFailure())
  }
}

export function* getParticipantLookSaga() {
  yield takeLatest(GET_PARTICIPANT_LOOK, getParticipantLookWorker)
}

// Update User Event Look Item - For Non-Paid Items
// ----------------------------------------------------------------------------

function* updateUserEventLookItemWorker({payload: {userEventLookItemId, transactionType}}) {
  try {
    const payload = yield api
      .updateUserEventLookItem(userEventLookItemId, {transaction_type: transactionType})
      .then(
        api.onStatus({
          OK: ({json}) => json,
          DEFAULT: ({statusText}) => {
            throw new Error(`Could not update look item: ${statusText}`)
          },
        }),
      )
    yield put(updateUserEventLookItemSuccess(payload))
  } catch (e) {
    console.error('Could not update look item.', e.message)
  }
}

export function* updateUserEventLookItemSaga() {
  yield takeLatest(UPDATE_USER_EVENT_LOOK_ITEM, updateUserEventLookItemWorker)
}

/**
 * Update's the name of the currently viewed look in the dashboard
 * @generator
 */
export function* updateLookNameWorker({payload: {lookId, lookName}}) {
  try {
    yield put(editLookNamePending())

    const response = yield call(api.editLookName, lookId, {look_name: lookName})

    const lookUpdate = api.onStatus({
      OK: ({json}) => json,
      403: ({statusText}) => {
        throw new Error(`CSRF Token Invalid: ${statusText}`)
      },
      DEFAULT: ({statusText}) => {
        throw new Error(`Could not change look's name: ${statusText}`)
      },
    })(response)

    yield put(editLookNameSuccess({lookId, lookName: lookUpdate.name}))
  } catch (e) {
    yield put(editLookNameFailure(new Error('Could not update look name.')))
  }
}

export function* updateLookNameSaga() {
  yield takeLatest(EDIT_LOOK_NAME, updateLookNameWorker)
}
